/**
 * EditSelectCell - Reusable single-select dropdown for DataGrid inline editing
 *
 * Uses MUI Select for clean dropdown rendering without dual scrollbar issues.
 *
 * Usage in column definition:
 * ```tsx
 * {
 *   field: 'status',
 *   editable: true,
 *   renderEditCell: (params) => (
 *     <EditSelectCell
 *       params={params}
 *       options={statusOptions}
 *       getOptionLabel={(opt) => opt.label}
 *       getOptionValue={(opt) => opt.type}
 *     />
 *   ),
 * }
 * ```
 */

import { Box, MenuItem, Select, Typography, useTheme } from '@mui/material'
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium'
import { useCallback, useState } from 'react'

interface EditSelectCellProps<T = any> {
  params: GridRenderEditCellParams
  options: T[]
  getOptionLabel: (option: T) => string
  getOptionValue: (option: T) => string
  placeholder?: string
  /** Optional: render color for each option */
  getOptionColor?: (option: T) => string | undefined
}

export const EditSelectCell = <T extends Record<string, any>>({
  params,
  options,
  getOptionLabel,
  getOptionValue,
  placeholder = 'Select...',
  getOptionColor,
}: EditSelectCellProps<T>) => {
  const apiRef = useGridApiContext()
  const theme = useTheme()
  const [open, setOpen] = useState(true) // Keep dropdown open initially

  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => {
    setOpen(false)
    apiRef.current.stopCellEditMode({ id: params.id, field: params.field })
  }, [apiRef, params.id, params.field])

  const handleChange = useCallback(
    (event: any) => {
      const newValue = event.target.value

      // Update the cell value
      apiRef.current.setEditCellValue({
        id: params.id,
        field: params.field,
        value: newValue,
      })

      // Exit edit mode after selection
      apiRef.current.stopCellEditMode({ id: params.id, field: params.field })
    },
    [apiRef, params.id, params.field]
  )

  return (
    <Select
      autoFocus
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={params.value || ''}
      onChange={handleChange}
      fullWidth
      displayEmpty
      size="small"
      sx={{
        bgcolor: 'transparent', // Transparent so cell highlight borders show through
        '& .MuiSelect-select': {
          bgcolor: 'transparent',
          py: 0.5,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none', // Remove the select's border so it doesn't compete with cell highlight
        },
      }}
      MenuProps={{
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        PaperProps: {
          sx: {
            bgcolor: theme.palette.background.paper,
            maxHeight: 250,
            minWidth: 180,
          },
        },
      }}
    >
      {!params.value && (
        <MenuItem value="" disabled>
          <Typography color="text.secondary">{placeholder}</Typography>
        </MenuItem>
      )}
      {options.map(option => {
        const value = getOptionValue(option)
        const label = getOptionLabel(option)
        const color = getOptionColor?.(option)
        return (
          <MenuItem key={value} value={value} sx={{ color }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>{label}</Typography>
            </Box>
          </MenuItem>
        )
      })}
    </Select>
  )
}

export default EditSelectCell
